import type { AlgoliaProduct } from "@lib/wrappers/algolia/types";
import {
    STORE_SIZES_AGG_SEPARATOR,
    STORES_FACET,
    getSizeFacetKey,
} from "@lib/constants/filtersConstants";

const FULL_SIZES_PREFIX = "fullSizes.";

type SizeRefinement = { attribute: string; value: string | number };

export type StoreSizeAggPair = { storeId: string; euSize: string };

export const normalizeStoreId = (storeId: string) => storeId.replace(/^[A-Z]{2}-/, "");

export const parseStoreSizeAggEntry = (entry: string): StoreSizeAggPair | null => {
    const separatorIndex = entry.lastIndexOf(STORE_SIZES_AGG_SEPARATOR);
    if (separatorIndex <= 0) return null;

    return {
        storeId: entry.slice(0, separatorIndex),
        euSize: entry.slice(separatorIndex + 1),
    };
};

const storeMatchesSelection = (storeId: string, selectedStoreIds: Set<string>) => {
    const normalized = normalizeStoreId(storeId);
    for (const selectedStoreId of selectedStoreIds) {
        if (normalizeStoreId(selectedStoreId) === normalized) return true;
    }
    return false;
};

const getSelectedSizeRefinements = (
    currentRefinements: { refinements: SizeRefinement[] }[]
): SizeRefinement[] =>
    currentRefinements.flatMap((group) =>
        group.refinements.filter((refinement) =>
            refinement.attribute?.startsWith(FULL_SIZES_PREFIX)
        )
    );

const getSelectedStoreRefinements = (
    currentRefinements: { refinements: SizeRefinement[] }[]
): SizeRefinement[] =>
    currentRefinements.flatMap((group) =>
        group.refinements.filter((refinement) => refinement.attribute === STORES_FACET)
    );

const resolveEuSizeKey = (
    refinement: SizeRefinement,
    products: AlgoliaProduct[]
): string | null => {
    const localeKey = refinement.attribute.replace(FULL_SIZES_PREFIX, "");
    if (localeKey === "eu") return String(refinement.value);

    for (const product of products) {
        const match = product.fullSizes?.find(
            (row) => row[localeKey as keyof typeof row] === String(refinement.value)
        );
        if (match?.eu != null) return String(match.eu);
    }
    return null;
};

const getSelectedEuSizeKeys = (
    selectedSizes: SizeRefinement[],
    products: AlgoliaProduct[]
): string[] => [
    ...new Set(
        selectedSizes
            .map((refinement) => resolveEuSizeKey(refinement, products))
            .filter((euSizeKey): euSizeKey is string => !!euSizeKey)
    ),
];

const mapEuSizesToLocaleValues = (
    euSizeKeys: Set<string>,
    products: AlgoliaProduct[],
    sizeLocale: string
): Set<string> => {
    const localeKey = getSizeFacetKey(sizeLocale);
    const localizedSizes = new Set<string>();

    for (const product of products) {
        for (const row of product.fullSizes ?? []) {
            if (!euSizeKeys.has(String(row.eu))) continue;

            const localizedSize = row[localeKey as keyof typeof row];
            if (localizedSize != null) {
                localizedSizes.add(String(localizedSize));
            }
        }
    }

    return localizedSizes;
};

/** Store IDs / locale sizes with stock when both store and size are selected.
 * Uses `storeSizesAgg` facet values (`storeId|euSize`). When only store or only size is selected,
 * returns null and the regular `stores` / `fullSizes.*` facets drive availability.
 */
export const getStoreSizeCrossFilters = (
    currentRefinements: { refinements: SizeRefinement[] }[],
    storeSizesAggValues: string[],
    sizeLocale: string,
    productsForSizeMapping: AlgoliaProduct[] = []
) => {
    let storeIdsForSelectedSizes: Set<string> | null = null;
    let sizeValuesForSelectedStores: Set<string> | null = null;

    const selectedSizes = getSelectedSizeRefinements(currentRefinements);
    const selectedStores = getSelectedStoreRefinements(currentRefinements);

    if (!selectedSizes.length || !selectedStores.length) {
        return { storeIdsForSelectedSizes, sizeValuesForSelectedStores };
    }

    const aggPairs = storeSizesAggValues
        .map(parseStoreSizeAggEntry)
        .filter((pair): pair is StoreSizeAggPair => pair != null);

    if (!aggPairs.length) {
        return { storeIdsForSelectedSizes, sizeValuesForSelectedStores };
    }

    const euSizeKeys = getSelectedEuSizeKeys(selectedSizes, productsForSizeMapping);
    if (!euSizeKeys.length) {
        return { storeIdsForSelectedSizes, sizeValuesForSelectedStores };
    }

    const selectedStoreIds = new Set(selectedStores.map((refinement) => String(refinement.value)));
    const euSizeKeySet = new Set(euSizeKeys);

    const matchingPairs = aggPairs.filter(
        (pair) =>
            euSizeKeySet.has(pair.euSize) && storeMatchesSelection(pair.storeId, selectedStoreIds)
    );

    if (!matchingPairs.length) {
        return { storeIdsForSelectedSizes, sizeValuesForSelectedStores };
    }

    storeIdsForSelectedSizes = new Set(matchingPairs.map((pair) => pair.storeId));

    const euSizesAtSelectedStores = new Set(matchingPairs.map((pair) => pair.euSize));
    sizeValuesForSelectedStores = mapEuSizesToLocaleValues(
        euSizesAtSelectedStores,
        productsForSizeMapping,
        sizeLocale
    );

    return { storeIdsForSelectedSizes, sizeValuesForSelectedStores };
};

export const isStoreAvailableForSelectedSizes = (
    storeValue: string,
    storeIdsForSelectedSizes: Set<string> | null
): boolean =>
    !storeIdsForSelectedSizes || storeMatchesSelection(storeValue, storeIdsForSelectedSizes);

export const filterStoreFacetOptionsBySize = <T extends { value: string }>(
    options: T[],
    storeIds: Set<string> | null
): T[] => {
    if (!storeIds) return options;
    return options.filter((option) => storeMatchesSelection(option.value, storeIds));
};

export const isSizeAvailableAtSelectedStores = (
    sizeValue: string,
    sizeValues: Set<string> | null
): boolean => !sizeValues || sizeValues.has(sizeValue);
